import type { components } from "@/api-schema";
import { SapExportStatus } from "@/api-schema-enums";
import { formatDateToLocale } from "@/common/helpers/format-date";
import { DeviceType } from "@/common/hooks/use-device";
import { CopyableValue } from "@/components/copyable-value/copyable-value";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import type { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import type { ImplementedColumnDef } from "@/components/data-table/types";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import i18next from "i18next";
import { Fragment } from "react";
import { getCustomerDisplayName } from "../customer-table/_helpers/get-customer-display-name";
import { getIdentifierRows } from "../customer-table/_helpers/get-identifier-rows";

type CustomerSapExportTableType = components["schemas"]["CustomerSapExport"] & RowWithIdentifier;

// TODO: Use global EmptyValue component, when this ticket is merged:
// https://linear.app/alphartis/issue/CLOUD-1767/extend-branch-brand-pivot-table
const EMPTY_VALUE_CELL = <span className="text-muted-foreground">—</span>;

export const customerSapExportTableDefaultColumns = {
  columnCustomerName: {
    id: "customerName",
    size: 220,
    enableSorting: true,
    header: () => i18next.t("common:Name"),
    accessorFn: (row: CustomerSapExportTableType) => (row.customer ? getCustomerDisplayName(row.customer) : undefined),
    cell: (info) => automaticCellRenderer(info.getValue()),
  } as ImplementedColumnDef<CustomerSapExportTableType>,

  columnIds: {
    id: "ids",
    size: 200,
    enableSorting: false,
    header: () => i18next.t("masterdata:Identifiers"),
    accessorFn: (row: CustomerSapExportTableType) => ({
      identifiers: row.customer?.customerIdentifiers,
    }),
    cell: (info) => {
      const rows = getIdentifierRows(info.getValue() as Parameters<typeof getIdentifierRows>[0]);

      if (rows.length === 0) {
        return EMPTY_VALUE_CELL;
      }

      return (
        <div className="grid grid-cols-[3.5rem_1fr] items-baseline gap-x-2 gap-y-1 text-left text-sm">
          {rows.map((row) => (
            <Fragment key={row.key}>
              <span className="text-muted-foreground text-xs">{row.label}</span>
              <CopyableValue value={row.value} />
            </Fragment>
          ))}
        </div>
      );
    },
  } as ImplementedColumnDef<CustomerSapExportTableType>,

  columnStatus: {
    id: "exportStatus",
    size: 140,
    enableSorting: true,
    header: () => i18next.t("masterdata:SapExportStatus"),
    accessorFn: (row: CustomerSapExportTableType) => row.exportStatus,
    cell: (info) => {
      const value = info.getValue() as SapExportStatus | null | undefined;

      if (!value) {
        return EMPTY_VALUE_CELL;
      }

      const isFailed = value === SapExportStatus.FAILED;

      return (
        <Badge
          variant={BadgeVariant.Outlined}
          colorVariant={isFailed ? BadgeColorVariant.Error : BadgeColorVariant.Success}
        >
          {isFailed ? i18next.t("masterdata:SapExportStatusFailed") : i18next.t("masterdata:SapExportStatusSuccess")}
        </Badge>
      );
    },
  } as ImplementedColumnDef<CustomerSapExportTableType>,

  columnCategory: {
    id: "category",
    size: 200,
    enableSorting: true,
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    header: () => i18next.t("aceSapExportIssues:ColumnCategory"),
    accessorFn: (row: CustomerSapExportTableType) => row.errorCategory,
    cell: (info) => {
      const value = info.getValue() as string | null | undefined;
      return value ? <span className="font-mono">{value}</span> : EMPTY_VALUE_CELL;
    },
  } as ImplementedColumnDef<CustomerSapExportTableType>,

  columnError: {
    id: "errorMessage",
    size: 380,
    enableSorting: false,
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    header: () => i18next.t("aceSapExportIssues:ColumnError"),
    accessorFn: (row: CustomerSapExportTableType) => row.errorMessage,
    cell: (info) => {
      const value = info.getValue() as string | null | undefined;
      return value ? <span className="text-left">{value}</span> : EMPTY_VALUE_CELL;
    },
  } as ImplementedColumnDef<CustomerSapExportTableType>,

  columnLastExportAt: {
    id: "lastExportAt",
    size: 180,
    enableSorting: true,
    header: () => i18next.t("aceSapExportIssues:LastExportAt"),
    accessorFn: (row: CustomerSapExportTableType) => row.lastExportAt,
    cell: (info) => {
      const value = info.getValue() as string;
      return value ? formatDateToLocale(value) : EMPTY_VALUE_CELL;
    },
  } as ImplementedColumnDef<CustomerSapExportTableType>,
};
